import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { useAuth } from "../lib/auth.jsx";

export function VerifyEmailScreen() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    session,
    verification,
    verificationLoading,
    refreshVerification,
    resendVerification,
    signOut,
  } = useAuth();
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const token = searchParams.get("token");
  const redirectedVerified = searchParams.get("verified") === "1";
  const redirectedError = searchParams.get("error");
  const redirectedEmail = searchParams.get("email");

  useEffect(() => {
    if (redirectedVerified) {
      setMessage("Your email is verified. You now have full access.");
      setError("");
      if (session) {
        refreshVerification().finally(() => {
          setTimeout(() => navigate("/", { replace: true }), 900);
        });
      }
      return;
    }
    if (redirectedError) {
      setError(redirectedError);
      setMessage("");
      return;
    }
    if (!token && session && verification?.verified !== true) {
      refreshVerification();
    }
  }, [token, redirectedVerified, redirectedError, session, verification?.verified]);

  useEffect(() => {
    if (!session || verificationLoading || verification?.verified !== true) return;
    navigate("/", { replace: true });
  }, [session, verificationLoading, verification?.verified, navigate]);

  useEffect(() => {
    if (!token) return;

    let cancelled = false;
    (async () => {
      setBusy(true);
      setError("");
      try {
        const res = await fetch(`/api/auth/verification/confirm?token=${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (!res.ok || !body.verified) {
          throw new Error(body.error || "Verification link is invalid or expired.");
        }
        if (cancelled) return;
        setMessage("Your email is verified. You now have full access.");
        if (session) {
          await refreshVerification();
          setTimeout(() => navigate("/", { replace: true }), 900);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || "Verification failed.");
      } finally {
        if (!cancelled) setBusy(false);
      }
    })();

    return () => { cancelled = true; };
  }, [token, session]);

  async function resend() {
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const status = await resendVerification();
      setMessage(status.message || "Confirmation email sent. The link expires in 5 minutes.");
    } catch (err) {
      setError(err.message || "Could not send confirmation email.");
    } finally {
      setBusy(false);
    }
  }

  const status = redirectedVerified ? "verified" : (verification?.status || "pending");
  const isPending = status === "pending";
  const expiresAt = verification?.expiresAt ? new Date(verification.expiresAt) : null;
  const isVerified = redirectedVerified || verification?.verified;
  const displayEmail = redirectedEmail || verification?.email || session?.user?.email || "your email address";

  return (
    <div style={{
      minHeight: "100vh",
      background: "var(--bg)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      padding: 16
    }}>
      <Card style={{ maxWidth: 480, width: "100%", margin: 0 }}>
        <SectionHeader
          emoji="✉️"
          title="Verify your email"
          subtitle="Confirm your email address before using your account."
        />

        <div style={{ display: "grid", gap: 14, marginTop: 20, fontSize: 14, color: "var(--text-muted)", lineHeight: 1.6 }}>
          {verificationLoading ? (
            <p>Checking verification status...</p>
          ) : isVerified ? (
            <p>Your email is verified. You can continue.</p>
          ) : (
            <>
              <p>
                We sent a confirmation email to <b style={{ color: "var(--text)" }}>{displayEmail}</b>.
                The link expires in 5 minutes.
              </p>
              {isPending && (
                <p>
                  This registration is currently marked as Pending. Request a new confirmation email to continue.
                </p>
              )}
              {expiresAt && !isPending && (
                <p>Current link expires at {expiresAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}.</p>
              )}
            </>
          )}

          {message && (
            <div style={{ color: "var(--text-confirm)", background: "var(--status-warning-bg)", border: "1px solid var(--border-strong)", borderRadius: 10, padding: "10px 12px" }}>
              {message}
            </div>
          )}
          {error && (
            <div role="alert" style={{ color: "var(--status-danger)", background: "var(--status-danger-bg)", border: "1px solid var(--status-danger-border)", borderRadius: 10, padding: "10px 12px" }}>
              {error}
            </div>
          )}

          <div style={{ display: "grid", gap: 10, marginTop: 6 }}>
            {isVerified ? (
              <Button label={session ? "Continue" : "Go to login"} onClick={() => navigate(session ? "/" : "/login", { replace: true })} />
            ) : (
              <Button label={busy ? "Sending..." : "Send confirmation email"} loading={busy} onClick={resend} disabled={!session} />
            )}
            <Button
              label={session ? "Use another account" : "Go to login"}
              variant="outline"
              onClick={async () => {
                if (session) await signOut();
                navigate("/login", { replace: true });
              }}
            />
          </div>
        </div>
      </Card>
    </div>
  );
}
